import { z } from 'zod'
import { TRAINER_CONTRACT_VERSION } from './trainer-event-read'

export const TRAINER_AFFILIATE_READ_CONTRACT_VERSION = TRAINER_CONTRACT_VERSION

const nullableString = z.string().nullable()
const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const trainerAffiliateReadQuerySchema = z.object({
  orgId: optionalQueryString,
  search: optionalQueryString,
  ownerUserId: optionalQueryString,
  owner_user_id: optionalQueryString,
  dateFrom: optionalQueryString,
  dateTo: optionalQueryString,
  identityStatus: optionalQueryString,
  payoutStatus: optionalQueryString,
}).passthrough()

export const trainerAffiliateReadSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  billingTouched: z.literal(false),
  moneyMoved: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  r2Touched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const trainerAffiliatePayoutHistorySideEffectsSchema = z.object({
  db1Read: z.boolean(),
  billingTouched: z.literal(false),
  moneyMoved: z.literal(false),
  paymentProviderTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  r2Touched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const trainerAffiliatePayoutApprovalAuthoritySchema = z.object({
  scope: z.literal('db1_review_only'),
  label: z.literal('DB1 review only'),
  description: z.literal('Records an internal payout review decision; it does not authorize provider execution, settlement, or money movement.'),
  finalPaymentAuthority: z.literal(false),
  settlementAuthority: z.literal(false),
  providerExecutionAuthority: z.literal(false),
}).passthrough()

export const trainerAffiliatePayoutLifecycleSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  status: z.enum(['eligible', 'identity_blocked', 'approval_pending', 'queued_dry_run', 'sent', 'failed', 'retrying', 'disputed', 'voided', 'provider_not_configured']),
  approvalRequired: z.boolean(),
  providerMode: z.enum(['dry_run', 'live_unavailable']),
  provider: z.literal('affiliate_payout'),
  outboxActionType: z.literal('affiliate.payout.execute'),
  outboxEnqueued: z.boolean(),
  moneyMoved: z.literal(false),
  paymentProviderTouched: z.literal(false),
  rawProviderPayloadReturned: z.literal(false),
  reason: z.string(),
}).passthrough()

export const trainerAffiliateItemSchema = z.object({
  id: z.number().finite(),
  users_id: z.number().finite(),
  referred_user_id: z.number().finite().nullable(),
  name: z.string(),
  email: z.string(),
  created_at: z.number().finite(),
  amount: z.number().finite(),
  date: nullableString,
  identity_status: z.enum(['resolved', 'unresolved']),
  payout_status: z.enum(['ready_for_review', 'identity_exception']),
  payout_lifecycle: trainerAffiliatePayoutLifecycleSchema,
}).passthrough()

export const trainerAffiliateLinkIntegritySchema = z.object({
  status: z.enum(['valid', 'invalid']),
  expectedHost: z.string(),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerAffiliateIdentityResolutionSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  sourceTable: z.literal('affiliates'),
  targetTable: z.literal('trainer_affiliates'),
  importedCount: z.number().int().nonnegative(),
  exceptionCount: z.number().int().nonnegative(),
  unresolvedIdentityCount: z.number().int().nonnegative(),
  lastExceptionAt: nullableString,
  importReady: z.boolean(),
  payoutGate: z.literal('closed'),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerAffiliateReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.union([
    z.tuple([z.literal('trainer_affiliates'), z.literal('trainer_xano_source_records')]),
    z.tuple([z.literal('trainer_affiliates'), z.literal('domain_audit_log'), z.literal('trainer_affiliate_payout_decisions')]),
  ]),
  projection: z.enum(['trainer-affiliate-summary-with-identity-resolution', 'trainer-affiliate-payout-history']),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerAffiliateListResponseSchema = z.object({
  contract_version: z.literal(TRAINER_AFFILIATE_READ_CONTRACT_VERSION),
  items: z.array(trainerAffiliateItemSchema),
  total_count: z.number().int().nonnegative(),
  total_amount: z.number().finite(),
  your_share_value: z.number().finite(),
  your_affiliate_link: z.string(),
  filters: z.object({
    search: z.string().optional(),
    ownerUserId: z.number().finite().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    identityStatus: z.enum(['all', 'resolved', 'unresolved']).optional(),
    payoutStatus: z.enum(['all', 'ready_for_review', 'identity_exception']).optional(),
  }).passthrough(),
  affiliateLinkIntegrity: trainerAffiliateLinkIntegritySchema,
  identityResolution: trainerAffiliateIdentityResolutionSchema,
  affiliateEvidence: trainerAffiliateReadEvidenceSchema.extend({
    tablesRead: z.tuple([z.literal('trainer_affiliates'), z.literal('trainer_xano_source_records')]),
    projection: z.literal('trainer-affiliate-summary-with-identity-resolution'),
  }).passthrough(),
  sideEffects: trainerAffiliateReadSideEffectsSchema,
}).passthrough()

export const trainerAffiliatePayoutDecisionSchema = z.object({
  id: z.string(),
  affiliateId: z.number().finite(),
  actorUserId: z.number().finite(),
  action: z.enum(['approve', 'dispute', 'void']),
  status: z.enum(['approved', 'disputed', 'voided']),
  reason: z.string(),
  idempotencyKey: z.string(),
  providerMode: z.literal('dry_run'),
  liveProviderAllowed: z.literal(false),
  moneyMoved: z.literal(false),
  paymentProviderTouched: z.literal(false),
  rawProviderPayloadReturned: z.literal(false),
  approvalAuthority: trainerAffiliatePayoutApprovalAuthoritySchema,
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const trainerAffiliatePayoutHistoryItemSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  actorUserId: z.number().finite().nullable(),
  requestedAction: z.enum(['approve', 'execute', 'retry', 'dispute', 'void']),
  status: z.enum(['eligible', 'identity_blocked', 'approval_pending', 'queued_dry_run', 'sent', 'failed', 'retrying', 'disputed', 'voided', 'provider_not_configured']),
  identityStatus: z.enum(['resolved', 'unresolved']),
  payoutStatus: z.enum(['ready_for_review', 'identity_exception']),
  outboxEnqueued: z.boolean(),
  idempotencyKey: z.string(),
  moneyMoved: z.literal(false),
  paymentProviderTouched: z.literal(false),
  rawProviderPayloadReturned: z.literal(false),
  decisionPersisted: z.boolean(),
  decisionStatus: z.enum(['approved', 'disputed', 'voided']).nullable(),
  approvalAuthority: trainerAffiliatePayoutApprovalAuthoritySchema,
}).passthrough()

export const trainerAffiliatePayoutHistoryResponseSchema = z.object({
  contract_version: z.literal(TRAINER_AFFILIATE_READ_CONTRACT_VERSION),
  affiliate: trainerAffiliateItemSchema,
  items: z.array(trainerAffiliatePayoutHistoryItemSchema),
  currentDecision: trainerAffiliatePayoutDecisionSchema.nullable(),
  decisionHistory: z.array(trainerAffiliatePayoutDecisionSchema),
  payoutHistoryEvidence: trainerAffiliateReadEvidenceSchema.extend({
    tablesRead: z.tuple([z.literal('trainer_affiliates'), z.literal('domain_audit_log'), z.literal('trainer_affiliate_payout_decisions')]),
    projection: z.literal('trainer-affiliate-payout-history'),
  }).passthrough(),
  sideEffects: trainerAffiliatePayoutHistorySideEffectsSchema,
}).passthrough()

export type TrainerAffiliateReadQuery = z.infer<typeof trainerAffiliateReadQuerySchema>
export type TrainerAffiliateReadSideEffects = z.infer<typeof trainerAffiliateReadSideEffectsSchema>
export type TrainerAffiliatePayoutApprovalAuthority = z.infer<typeof trainerAffiliatePayoutApprovalAuthoritySchema>
export type TrainerAffiliatePayoutLifecycle = z.infer<typeof trainerAffiliatePayoutLifecycleSchema>
export type TrainerAffiliateItem = z.infer<typeof trainerAffiliateItemSchema>
export type TrainerAffiliateLinkIntegrity = z.infer<typeof trainerAffiliateLinkIntegritySchema>
export type TrainerAffiliateIdentityResolution = z.infer<typeof trainerAffiliateIdentityResolutionSchema>
export type TrainerAffiliateReadEvidence = z.infer<typeof trainerAffiliateReadEvidenceSchema>
export type TrainerAffiliateListResponse = z.infer<typeof trainerAffiliateListResponseSchema>
export type TrainerAffiliatePayoutDecision = z.infer<typeof trainerAffiliatePayoutDecisionSchema>
export type TrainerAffiliatePayoutHistoryItem = z.infer<typeof trainerAffiliatePayoutHistoryItemSchema>
export type TrainerAffiliatePayoutHistoryResponse = z.infer<typeof trainerAffiliatePayoutHistoryResponseSchema>
